import React, { useState } from 'react';
import { Button, Input, List, Popconfirm, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { createScreen, screenHasDynamicFields } from 'utils/metaFlowComponents';
import Flex from 'components/shared-components/Flex';
import { PRIMARY } from '../../CustomUtils.js/utils';

const { Text } = Typography;

const ScreenListPanel = ({
  screens,
  activeScreenId,
  isDynamic = false,
  onSelectScreen,
  onAddScreen,
  onDeleteScreen,
  onMoveScreen,
  onRenameScreen,
  onOpenScreenSettings,
}) => {
  const [newScreenName, setNewScreenName] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAdd = () => {
    const title = newScreenName.trim() || `Screen ${screens.length + 1}`;
    onAddScreen(createScreen(title, screens.length));
    setNewScreenName('');
    setAdding(false);
  };

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 12 }}>
        Screens
      </Text>
      <List
        size="small"
        dataSource={screens}
        locale={{ emptyText: 'No screens yet' }}
        renderItem={(screen, index) => {
          const isActive = screen.id === activeScreenId;
          const isNextActive = screens[index + 1]?.id === activeScreenId;
          const isFlowActive = isActive || isNextActive;
          const isLast = index === screens.length - 1;
          const showSettings = isDynamic && screenHasDynamicFields(screen);

          return (
            <React.Fragment key={screen.id}>
              <List.Item
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '8px 10px',
                  marginBottom: isLast ? 0 : 4,
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: isActive ? '#e6f4ff' : '#fafafa',
                  border: isActive ? '1px solid #91caff' : '1px solid #f0f0f0',
                }}
                onClick={() => onSelectScreen(screen.id)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Input
                    size="small"
                    bordered={false}
                    value={screen.title}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onRenameScreen(screen.id, { title: e.target.value })}
                    style={{ fontWeight: isActive ? 600 : 400, padding: 0 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                    {screen.id}
                  </Text>
                  {showSettings && screen.tenant_api_uri ? (
                    <Text type="success" style={{ fontSize: 10, display: 'block', marginTop: 2 }}>
                      API configured
                    </Text>
                  ) : null}
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    flexShrink: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {screens.length > 1 && (
                    <Button
                      key="up"
                      type="text"
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={index === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveScreen?.(screen.id, -1);
                      }}
                      style={{ width: 28, height: 28, padding: 0 }}
                    />
                  )}
                  {screens.length > 1 && (
                    <Button
                      key="down"
                      type="text"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={isLast}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveScreen?.(screen.id, 1);
                      }}
                      style={{ width: 28, height: 28, padding: 0 }}
                    />
                  )}
                  {showSettings && (
                    <Button
                      key="settings"
                      type="text"
                      size="small"
                      icon={<SettingOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenScreenSettings?.(screen.id);
                      }}
                      style={{ width: 28, height: 28, padding: 0 }}
                    />
                  )}
                  {screens.length > 1 && (
                    <Popconfirm
                      key="delete"
                      title="Delete this screen?"
                      onConfirm={(e) => {
                        e?.stopPropagation();
                        onDeleteScreen(screen.id);
                      }}
                      onCancel={(e) => e?.stopPropagation()}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 28, height: 28, padding: 0 }}
                      />
                    </Popconfirm>
                  )}
                </div>
              </List.Item>
              {!isLast && (
                <Flex justifyContent={'center'} alignItems={'center'} className='my-2'>
                  <Button size='small' className={'rounded-circle'}>
                    <ArrowDownOutlined
                      style={{
                        fontSize: '16px',
                        padding: '0 2px',
                        color: isFlowActive ? PRIMARY : '#bfbfbf',
                      }}
                    />
                  </Button>
                </Flex>
              )}
            </React.Fragment>
          );
        }}
      />
      {adding ? (
        <div style={{ marginTop: 8 }}>
          <Input
            size="small"
            placeholder="Screen name"
            value={newScreenName}
            onChange={(e) => setNewScreenName(e.target.value)}
            onPressEnter={handleAdd}
            autoFocus
          />
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <Button size="small" type="primary" onClick={handleAdd}>
              Add
            </Button>
            <Button size="small" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="dashed"
          block
          size="small"
          icon={<PlusOutlined />}
          style={{ marginTop: 8 }}
          onClick={() => setAdding(true)}
          disabled={screens.length >= 20}
        >
          Add screen
        </Button>
      )}
    </div>
  );
};

export default ScreenListPanel;
